import { useCallback, useEffect, useState } from 'react';

/**
 * Configuration options for the inactivity timer hook
 */
interface UseInactivityTimerOptions {
	/** Time in seconds before inactivity warning is shown */
	inactivityTimeout?: number;
	/** Time in seconds for the warning countdown */
	warningTimeout?: number;
	/** Optional callback when inactivity warning starts */
	onInactivityWarning?: () => void;
	/** Optional callback when inactivity timer expires */
	onInactivityExpired?: () => void;
}

/**
 * Return type for the inactivity timer hook
 */
interface UseInactivityTimerReturn {
	/** Whether the inactivity warning is currently active */
	isWarningActive: boolean;
	/** Current countdown value in seconds */
	countdown: number;
	/** Function to reset the activity timer */
	resetTimer: () => void;
	/** Function to manually start the warning */
	startWarning: () => void;
	/** Function to stop the warning */
	stopWarning: () => void;
}

/** Timer reference for cleanup */
type TimerReference = NodeJS.Timeout | null;

/** DOM events that indicate user activity */
const USER_ACTIVITY_EVENTS = ['mousedown', 'keypress', 'scroll', 'mousemove', 'touchstart'] as const;

/** Default inactivity timeout - 5 minutes */
const DEFAULT_INACTIVITY_TIMEOUT = 5 * 60; // 5 minutes in seconds

/** Default warning timeout - 30 seconds */
const DEFAULT_WARNING_TIMEOUT = 30; // 30 seconds

/** Interval for checking inactivity - check every 30 seconds */
const INACTIVITY_CHECK_INTERVAL = 30 * 1000; // 30 seconds in milliseconds

/** Interval for updating countdown display - 1 second */
const COUNTDOWN_UPDATE_INTERVAL = 1000;

/**
 * Creates a countdown timer that updates every second
 * @param expirationTimestamp - When the countdown should expire
 * @param onExpired - Callback when countdown reaches zero
 * @param updateCountdown - Function to update countdown display
 * @returns Timer reference for cleanup
 */
const createCountdownTimer = (
	expirationTimestamp: number,
	onExpired: () => void,
	updateCountdown: (seconds: number) => void,
): TimerReference => {
	return setInterval(() => {
		const currentTimestamp = Math.floor(Date.now() / 1000);
		const remainingSeconds = Math.max(0, expirationTimestamp - currentTimestamp);

		updateCountdown(remainingSeconds);

		if (remainingSeconds <= 0) {
			onExpired();
		}
	}, COUNTDOWN_UPDATE_INTERVAL);
};

/**
 * Creates a timer that continuously checks for user inactivity
 * @param getLastActivityTimestamp - Function to get current last activity timestamp
 * @param inactivityThreshold - Seconds before triggering warning
 * @param onInactivityDetected - Callback when inactivity is detected
 * @returns Timer reference for cleanup
 */
const createInactivityChecker = (
	getLastActivityTimestamp: () => number,
	inactivityThreshold: number,
	onInactivityDetected: () => void,
): TimerReference => {
	return setInterval(() => {
		const currentTimestamp = Math.floor(Date.now() / 1000);
		const lastActivityTimestamp = getLastActivityTimestamp();
		const secondsSinceLastActivity = currentTimestamp - lastActivityTimestamp;

		if (secondsSinceLastActivity >= inactivityThreshold) {
			onInactivityDetected();
		}
	}, INACTIVITY_CHECK_INTERVAL);
};

/**
 * Sets up event listeners for user activity detection
 * @param onUserActivity - Callback when user activity is detected
 * @returns Cleanup function to remove event listeners
 */
const setupUserActivityDetection = (onUserActivity: () => void): (() => void) => {
	const handleUserActivity = () => {
		onUserActivity();
	};

	// Add event listeners for all activity events
	USER_ACTIVITY_EVENTS.forEach((eventType) => {
		window.addEventListener(eventType, handleUserActivity);
	});

	// Return cleanup function
	return () => {
		USER_ACTIVITY_EVENTS.forEach((eventType) => {
			window.removeEventListener(eventType, handleUserActivity);
		});
	};
};

/**
 * Custom hook for managing user inactivity detection and warning countdown
 *
 * Continuously monitors user activity and shows a warning when inactivity threshold is exceeded.
 * Provides a 30-second countdown for the user to confirm staying logged in before expiration.
 *
 * @param options - Configuration options for inactivity detection
 * @returns Object with warning state, countdown, and control functions
 *
 * @example
 * ```tsx
 * const { isWarningActive, countdown, resetTimer } = useInactivityTimer({
 *   inactivityTimeout: 300, // 5 minutes (default)
 *   warningTimeout: 30,     // 30 seconds (default)
 *   onInactivityExpired: () => logout(),
 * });
 * ```
 */
export const useInactivityTimer = ({
	inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT,
	warningTimeout = DEFAULT_WARNING_TIMEOUT,
	onInactivityWarning,
	onInactivityExpired,
}: UseInactivityTimerOptions): UseInactivityTimerReturn => {
	// Track when user was last active - initialized to current onInactivityExpired
	const [lastActivityTimestamp, setLastActivityTimestamp] = useState<number>(Math.floor(Date.now() / 1000));

	// Control warning modal display
	const [isWarningActive, setIsWarningActive] = useState<boolean>(false);

	// Current countdown value for display
	const [countdown, setCountdown] = useState<number>(0);

	// When the warning period expires
	const [warningExpirationTimestamp, setWarningExpirationTimestamp] = useState<number | null>(null);

	/**
	 * Resets the activity timer and clears any active warnings
	 * Called when user activity is detected or user chooses to stay logged in
	 */
	const resetTimer = useCallback(() => {
		const currentTimestamp = Math.floor(Date.now() / 1000);
		setLastActivityTimestamp(currentTimestamp);
		setIsWarningActive(false);
		setWarningExpirationTimestamp(null);
		setCountdown(0);
	}, []);

	/**
	 * Starts the inactivity warning countdown
	 * Called when inactivity threshold is exceeded
	 */
	const startWarning = useCallback(() => {
		// Only start warning if not already active
		if (!isWarningActive) {
			const currentTimestamp = Math.floor(Date.now() / 1000);
			const expirationTimestamp = currentTimestamp + warningTimeout;

			setWarningExpirationTimestamp(expirationTimestamp);
			setIsWarningActive(true);

			onInactivityWarning?.();
		}
	}, [isWarningActive, warningTimeout, onInactivityWarning]);

	/**
	 * Stops the inactivity warning and resets warning state
	 * Called when user manually dismisses the warning
	 */
	const stopWarning = useCallback(() => {
		setIsWarningActive(false);
		setWarningExpirationTimestamp(null);
		setCountdown(0);
	}, []);

	/**
	 * Effect: Manages the countdown timer when warning is active
	 * Updates countdown display every second and handles expiration
	 */
	useEffect(() => {
		let countdownTimer: TimerReference = null;

		if (isWarningActive && warningExpirationTimestamp) {
			const handleCountdownExpired = () => {
				if (countdownTimer) {
					clearInterval(countdownTimer);
				}
				setIsWarningActive(false);
				setWarningExpirationTimestamp(null);
				setCountdown(0);
				onInactivityExpired?.();
			};

			countdownTimer = createCountdownTimer(warningExpirationTimestamp, handleCountdownExpired, setCountdown);
		}

		return () => {
			if (countdownTimer) {
				clearInterval(countdownTimer);
			}
		};
	}, [isWarningActive, warningExpirationTimestamp, onInactivityExpired]);

	/**
	 * Effect: Sets up continuous user activity detection and inactivity checking
	 * Monitors user activity and triggers warning when inactivity threshold is exceeded
	 */
	useEffect(() => {
		const handleUserActivity = () => {
			// Only reset timer if warning is not currently active
			// This prevents resetting during the 30-second confirmation window
			if (!isWarningActive) {
				resetTimer();
			}
		};

		const handleInactivityDetected = () => {
			// Start warning if not already active
			startWarning();
		};

		// Set up user activity detection
		const cleanupActivityDetection = setupUserActivityDetection(handleUserActivity);

		// Set up continuous inactivity checking
		// Use a function to get the current timestamp to avoid stale closure
		const getLastActivityTimestamp = () => lastActivityTimestamp;
		const inactivityCheckTimer = createInactivityChecker(
			getLastActivityTimestamp,
			inactivityTimeout,
			handleInactivityDetected,
		);

		// Cleanup function
		return () => {
			cleanupActivityDetection();

			if (inactivityCheckTimer) {
				clearInterval(inactivityCheckTimer);
			}
		};
	}, [lastActivityTimestamp, inactivityTimeout, isWarningActive, resetTimer, startWarning]);

	return {
		isWarningActive,
		countdown,
		resetTimer,
		startWarning,
		stopWarning,
	};
};
